/**
 * InfoContent - Static contextual help content for each page.
 *
 * Used by InfoSidebar to display page-specific guidance.
 */

import type { InfoSection } from '../components/InfoSidebar';

interface PageInfo {
  title: string;
  description?: string;
  sections: InfoSection[];
}

export const INFO_CONTENT: Record<string, PageInfo> = {
  home: {
    title: 'About Subscrypts',
    description: 'Learn about the Subscrypts Discord Bot.',
    sections: [
      {
        heading: 'What is Subscrypts?',
        body: 'Subscrypts is a blockchain-powered subscription service built on Arbitrum. Discord server owners (merchants) create subscription plans, and members subscribe using their Ethereum wallets to gain access to exclusive roles and channels.',
      },
      {
        heading: 'How It Works',
        body: 'Merchants create subscription plans on-chain with custom pricing, duration, and currency (SUBS or USDC). Subscribers connect their wallet, pay the subscription fee, and the Discord bot automatically grants the corresponding roles. Roles are reconciled periodically to ensure only active subscribers retain access.',
      },
      {
        heading: 'Getting Started',
        body: 'To set up Subscrypts for your Discord server: 1) Add the bot to your server using the "Add to Discord" button. 2) Run /subs-admin setup in your server. 3) Create a subscription plan via the web interface. 4) Map the plan to a Discord role using /subs-admin map. Your members can then subscribe through the generated link.',
      },
      {
        heading: 'Pricing & Fees',
        body: 'There is a small one-time ETH fee to create a subscription plan on-chain. Subscription payments are made in SUBS tokens or USDC on the Arbitrum network, which means very low gas fees. Merchants set their own subscription pricing.',
      },
    ],
  },

  subscribe: {
    title: 'Subscribe',
    description: 'How subscribing works.',
    sections: [
      {
        heading: 'What is Subscribing?',
        body: 'Subscribing means paying a blockchain-based subscription fee to a merchant\'s plan. Once your payment is confirmed on-chain, the Discord bot automatically grants you the corresponding role(s) in the Discord server.',
      },
      {
        heading: 'How Payments Work',
        body: 'Payments are made on the Arbitrum network. You\'ll need a connected wallet (like MetaMask) with sufficient SUBS or USDC tokens. The transaction is executed directly with the Subscrypts smart contract — no intermediaries.',
      },
      {
        heading: 'USDC Fallback',
        body: 'If your SUBS token balance is too low to cover the subscription fee, the system can automatically use USDC to cover the shortfall. This swap happens on-chain via Uniswap V3 during the subscription transaction.',
      },
      {
        heading: 'Recurring Cycles',
        body: 'You can choose to subscribe for multiple cycles upfront. Each cycle extends your subscription by the plan\'s duration (e.g., 1 month). Subscribing for more cycles means fewer future transactions. You can also enable recurring payments to auto-renew.',
      },
      {
        heading: 'Referral Addresses',
        body: 'Some plans offer a referral bonus. If someone referred you, enter their wallet address in the referral field. Both you and the referrer may receive bonus subscription time, as configured by the merchant.',
      },
      {
        heading: 'What Roles You Get',
        body: 'After subscribing, the Discord bot checks your on-chain subscription status and grants the mapped Discord role(s). These roles give you access to exclusive channels and features. Roles are automatically removed when your subscription expires.',
      },
      {
        heading: 'Why Subscribe URLs Change',
        body: 'Each subscribe URL contains a version number and a cryptographic signature. When a server admin updates subscription plans or role mappings, a new version is generated with a fresh signature. This prevents anyone from tampering with the subscribe page — the data you see is guaranteed to match what the admin configured. If you visit an older link, you\'ll be automatically redirected to the latest version.',
      },
    ],
  },

  swap: {
    title: 'Token Swap',
    description: 'How swapping SUBS tokens works.',
    sections: [
      {
        heading: 'What is Swapping?',
        body: 'The swap page lets you buy or sell SUBS tokens using USDC. This is useful for acquiring SUBS tokens needed for subscriptions, or converting SUBS back to USDC.',
      },
      {
        heading: 'How the Swap Works',
        body: 'Swaps are executed on-chain via Uniswap V3 on Arbitrum. You\'ll first approve the token spend, then the swap is executed through the decentralized exchange router. The entire process happens in your wallet.',
      },
      {
        heading: 'Slippage Tolerance',
        body: 'A 1% slippage tolerance is applied to protect you from price movement during the transaction. This means the actual amount received may vary slightly from the quoted amount, but never by more than 1%.',
      },
      {
        heading: 'Where Tokens Go',
        body: 'Swapped tokens go directly to your connected wallet address. SUBS tokens can then be used to subscribe to plans. The swap is non-custodial — tokens move directly between the liquidity pool and your wallet.',
      },
    ],
  },

  createSubscription: {
    title: 'Create Plan',
    description: 'How to create a subscription plan.',
    sections: [
      {
        heading: 'What is a Plan?',
        body: 'A plan is a subscription offer you create as a merchant. It defines the price, currency, duration, and other settings. Subscribers will see this plan and can subscribe to it. Each plan gets a unique on-chain ID.',
      },
      {
        heading: 'Pricing Model',
        body: 'Set the subscription amount in either SUBS or USDC tokens. The amount is per cycle — if your plan duration is 1 month and amount is 10 SUBS, subscribers pay 10 SUBS per month.',
      },
      {
        heading: 'Description & Attributes',
        body: 'The description and attribute fields are stored on-chain as bytes32 values (max 32 bytes / ~32 ASCII characters). Use the description for a human-readable plan name and attributes for metadata tags.',
      },
      {
        heading: 'Duration',
        body: 'Choose from presets (1 week, 1 month, 1 year) or set a custom duration. The duration defines one subscription cycle. Subscribers can purchase multiple cycles at once.',
      },
      {
        heading: 'Referral Bonus',
        body: 'Optionally set a referral bonus duration. When a subscriber is referred by another wallet, both parties can receive this bonus time added to their subscription. Set to 0 to disable referrals.',
      },
    ],
  },

  link: {
    title: 'Wallet Linking',
    description: 'How wallet linking works.',
    sections: [
      {
        heading: 'What is Wallet Linking?',
        body: 'Wallet linking connects your Ethereum wallet address to your Discord account within a specific server. This allows the bot to verify your on-chain subscriptions and grant the appropriate Discord roles.',
      },
      {
        heading: 'Why Sign a Message?',
        body: 'You\'ll be asked to sign a message (SIWE — Sign-In with Ethereum) to prove you own the wallet address. This is a free, gasless operation that doesn\'t send any transaction. It\'s a cryptographic proof of ownership.',
      },
      {
        heading: 'What Data is Stored',
        body: 'The bot stores the association between your Discord user ID and your wallet address for the specific guild. No private keys or sensitive wallet data are stored — only the public address and the signed verification.',
      },
      {
        heading: 'How to Unlink',
        body: 'You can unlink your wallet at any time by using the /subs unlink command in the Discord server. This removes the association and the bot will no longer check your subscriptions for that guild.',
      },
    ],
  },
};
